import React, { useEffect, useRef, useState } from "react";
import { Trash2, Plus, Shield, Upload, RefreshCcw, BarChart3, Film, Tv, Users, Clock, Star, X, Radio, Award, User2, Check, Ban, Search as SearchIcon, KeyRound, Mail, Crown, Clapperboard, Bell, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

/* ---------------- Overview ---------------- */
export default function Overview() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(r => setS(r.data)).catch(() => toast.error("Stats failed")); }, []);
  if (!s) return <p className="text-gray-400">Loading…</p>;
  const cards = [
    { label: "Users", value: s.users, icon: Users, accent: "text-blue-400" },
    { label: "Active subscribers", value: s.active_subscribers, icon: Users, accent: "text-green-400" },
    { label: "Watch hours", value: s.watch_hours, icon: Clock, accent: "text-yellow-400" },
    { label: "Movies", value: s.movies, icon: Film, accent: "text-[#E50914]" },
    { label: "Series", value: s.series, icon: Tv, accent: "text-pink-400" },
    { label: "Episodes", value: s.episodes, icon: Tv, accent: "text-indigo-400" },
    { label: "Reviews", value: s.reviews, icon: Star, accent: "text-amber-400" },
    { label: "Watchlist items", value: s.watchlist_items, icon: Plus, accent: "text-gray-400" },
  ];
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map(c => (
          <div key={c.label} data-testid={`stat-${c.label.toLowerCase().replace(/\s/g, "-")}`} className="bg-[#141414] border border-white/5 rounded-lg p-5">
            <div className="flex items-center gap-2 mb-2">
              <c.icon className={`w-4 h-4 ${c.accent}`} />
              <span className="text-xs uppercase tracking-widest text-gray-500">{c.label}</span>
            </div>
            <div className="text-3xl font-black tabular-nums">{c.value}</div>
          </div>
        ))}
      </div>
      <div>
        <h3 className="text-lg font-semibold mb-3">Top titles by viewers</h3>
        {s.top_titles.length === 0 ? (
          <p className="text-gray-500 text-sm">Not enough data yet.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {s.top_titles.map((t, i) => (
              <div key={t.id} className="bg-[#141414] border border-white/5 rounded-lg p-3">
                <div className="flex items-start gap-3">
                  <img src={t.poster} alt="" className="w-14 h-20 object-cover rounded" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-gray-500">#{i + 1}</div>
                    <div className="font-semibold text-sm line-clamp-2">{t.title}</div>
                    <div className="text-xs text-gray-400 mt-1">{t.viewers} viewer{t.viewers !== 1 ? "s" : ""}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <ABHeadlineWidget />
      <JuryPanelNudge />
      <FailedLoginsWidget />
    </div>
  );
}

function JuryPanelNudge() {
  const [jury, setJury] = useState(null);
  useEffect(() => {
    api.get("/jury/list").then((r) => setJury(r.data?.jury || [])).catch(() => setJury([]));
  }, []);
  if (jury === null) return null;
  const target = 6;
  if (jury.length >= target) return null; // Grid is full enough
  const missing = target - jury.length;
  return (
    <div
      data-testid="jury-panel-nudge"
      className="rounded-lg border border-amber-400/30 bg-gradient-to-br from-amber-500/10 via-[#141414] to-[#141414] p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-amber-400/20 flex items-center justify-center flex-shrink-0">
          <Award className="w-5 h-5 text-amber-300" />
        </div>
        <div>
          <div className="text-sm font-bold text-amber-200">
            Publish {missing} more juror{missing === 1 ? "" : "s"} to fill your festival panel
          </div>
          <div className="text-xs text-gray-400 mt-1 max-w-2xl">
            Filmmakers see this panel on <span className="font-mono text-amber-300/80">/festival</span> and decide whether to submit their film based on the credibility of your jury. You currently have {jury.length} published juror{jury.length === 1 ? "" : "s"} — a fuller grid dramatically improves festival trust and submissions.
          </div>
        </div>
      </div>
      <a
        href="/admin?tab=festivals"
        data-testid="jury-panel-nudge-cta"
        className="btn-red inline-flex items-center gap-2 px-4 py-2 rounded text-sm font-semibold whitespace-nowrap"
      >
        <Plus className="w-4 h-4" /> Add juror
      </a>
    </div>
  );
}

/* ---------------- A/B Headline Test Widget ---------------- */
function ABHeadlineWidget() {
  const [data, setData] = useState(null);
  const load = () => api.get("/admin/landing/ab-stats").then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(); }, []);
  if (!data) return null;
  const rows = [["A", data.A], ["B", data.B]];
  const winner = (() => {
    if (!data.A.impressions && !data.B.impressions) return null;
    return (data.A.conv_pct || 0) >= (data.B.conv_pct || 0) ? "A" : "B";
  })();
  return (
    <div className="bg-[#141414] border border-white/5 rounded-lg p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-amber-300" />
          <h3 className="text-lg font-semibold">Landing — Headline A/B test</h3>
        </div>
        <button
          data-testid="ab-refresh-btn"
          onClick={load}
          className="text-xs text-gray-400 hover:text-white flex items-center gap-1.5"
        >
          <RefreshCcw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {rows.map(([key, v]) => (
          <div
            key={key}
            data-testid={`ab-variant-${key}`}
            className={`rounded-lg p-4 border ${winner === key ? "border-emerald-500/50 bg-emerald-500/5" : "border-white/10 bg-white/[0.02]"}`}
          >
            <div className="flex items-center gap-2 mb-2">
              <span className={`text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded ${winner === key ? "bg-emerald-500/20 text-emerald-300" : "bg-white/10 text-gray-300"}`}>
                Variant {key}{winner === key && v.impressions > 0 ? " · Leading" : ""}
              </span>
            </div>
            <p className="text-sm text-gray-300 italic mb-3">&ldquo;{v.headline}&rdquo;</p>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div><div className="text-2xl font-black tabular-nums">{v.impressions}</div><div className="text-[10px] uppercase text-gray-500 tracking-widest">Views</div></div>
              <div><div className="text-2xl font-black tabular-nums">{v.signup_clicks}</div><div className="text-[10px] uppercase text-gray-500 tracking-widest">CTA clicks</div></div>
              <div><div className="text-2xl font-black tabular-nums text-emerald-400">{v.signups}</div><div className="text-[10px] uppercase text-gray-500 tracking-widest">Signups</div></div>
            </div>
            <div className="mt-3 pt-3 border-t border-white/5 flex justify-between text-xs">
              <span className="text-gray-500">CTR <span className="text-gray-300 font-semibold">{v.ctr_pct}%</span></span>
              <span className="text-gray-500">Conv <span className="text-emerald-300 font-semibold">{v.conv_pct}%</span></span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Failed Logins Widget (proactive lockout monitor) ---------------- */
function FailedLoginsWidget() {
  const [hours, setHours] = useState(24);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (h = hours) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/login-failures", { params: { hours: h, limit: 25 } });
      setData(data);
    } catch (e) {
      console.error("[failed-logins] load failed", e?.response?.status);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(hours); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [hours]);

  const reasonLabel = (r) => {
    if (r === "wrong_password") return { label: "Wrong password", color: "text-amber-300 border-amber-500/30 bg-amber-500/10" };
    if (r === "unknown_email") return { label: "Unknown email", color: "text-gray-400 border-white/15 bg-white/5" };
    if (r === "no_password") return { label: "Google-only acct", color: "text-blue-300 border-blue-500/30 bg-blue-500/10" };
    return { label: r, color: "text-gray-400 border-white/15 bg-white/5" };
  };

  return (
    <div data-testid="failed-logins-widget" className="bg-[#141414] border border-white/5 rounded-lg p-5">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-[#E50914]" />
          <h3 className="text-lg font-semibold">Recent failed sign-ins</h3>
          <span className="text-xs text-gray-500">(spot lockouts before they complain)</span>
        </div>
        <div className="flex items-center gap-2 text-xs">
          {[1, 24, 168].map((h) => (
            <button
              key={h}
              data-testid={`failed-logins-window-${h}`}
              onClick={() => setHours(h)}
              className={`px-2.5 py-1 rounded transition-colors ${hours === h ? "bg-[#E50914] text-white" : "text-gray-400 hover:text-white border border-white/10 hover:border-white/30"}`}
            >
              {h === 1 ? "1 hr" : h === 24 ? "24 hr" : "7 days"}
            </button>
          ))}
          <button
            onClick={() => load(hours)}
            disabled={loading}
            data-testid="failed-logins-refresh"
            className="ml-1 p-1.5 rounded hover:bg-white/5 text-gray-400 hover:text-white"
            title="Refresh"
          >
            <RefreshCcw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {!data ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : data.rows.length === 0 ? (
        <p className="text-sm text-gray-500 py-6 text-center">
          🎉 No failed sign-ins in the last {hours === 1 ? "hour" : hours === 24 ? "24 hours" : "7 days"}.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-gray-500 border-b border-white/10">
              <tr>
                <th className="text-left py-2 pr-3">Email</th>
                <th className="text-left py-2 pr-3">Attempts</th>
                <th className="text-left py-2 pr-3 hidden md:table-cell">Reason</th>
                <th className="text-left py-2 pr-3 hidden md:table-cell">Distinct IPs</th>
                <th className="text-left py-2 pr-3">Last attempt</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.email} className="border-b border-white/[0.05]" data-testid={`failed-login-row-${r.email}`}>
                  <td className="py-2.5 pr-3 font-mono text-xs truncate max-w-[200px]" title={r.email}>{r.email}</td>
                  <td className="py-2.5 pr-3">
                    <span className={`font-bold ${r.attempts >= 5 ? "text-red-400" : r.attempts >= 3 ? "text-amber-300" : "text-gray-300"}`}>
                      {r.attempts}
                    </span>
                  </td>
                  <td className="py-2.5 pr-3 hidden md:table-cell">
                    <div className="flex flex-wrap gap-1">
                      {(r.reasons || []).map((reason) => {
                        const meta = reasonLabel(reason);
                        return (
                          <span key={reason} className={`text-[10px] px-1.5 py-0.5 rounded border ${meta.color}`}>{meta.label}</span>
                        );
                      })}
                    </div>
                  </td>
                  <td className="py-2.5 pr-3 hidden md:table-cell text-xs text-gray-400">
                    {r.distinct_ips}
                    {r.distinct_ips >= 3 && <span className="ml-1 text-red-400 font-bold" title="Multiple IPs — possible brute force">⚠</span>}
                  </td>
                  <td className="py-2.5 pr-3 text-xs text-gray-400">
                    {new Date(r.last_attempt).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[11px] text-gray-500 mt-3">
            💡 Click <strong>Users</strong> tab → search a locked-out email → <strong>Reset password</strong> or <strong>Generate reset link</strong> to unblock them.
          </p>
        </div>
      )}
    </div>
  );
}
